import React from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  styled,
  StepConnector
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const CustomStepper = styled(Stepper)(({ theme }) => ({
  width: '100%',
  '& .MuiStepConnector-line': {
    borderColor: theme.palette.grey[300],
  },
  '& .MuiStepLabel-label': {
    marginTop: theme.spacing(1),
    fontSize: '13px',
    color: theme.palette.text.primary,
    textAlign: 'center',
    lineHeight: 1.2,
  },
  '& .MuiStepLabel-iconContainer': {
    paddingRight: 0
  }
}));

/**
 * A reusable stepper component that shows progress through a series of steps
 * @param {Object} props
 * @param {Array} props.steps - Array of step objects with { id, label }
 * @param {Object} props.stepsStatus - Object mapping step ids to their status ('completed', 'in_progress', or 'todo')
 */
const AppStatusStepper = ({ steps, stepsStatus }) => {
  // Helper function to determine step state
  const getStepState = (stepId) => {
    const status = stepsStatus?.[stepId];
    switch (status) {
      case 'completed':
        return 'completed';
      case 'in_progress':
        return 'active';
      default:
        return 'disabled';
    }
  };

  // Custom icons for different states
  const StepIcon = ({ active, completed }) => {
    const iconStyle = {
      fontSize: 20,
      width: 20,
      height: 20
    };

    if (completed) {
      return <CheckCircleIcon sx={{ ...iconStyle, color: (theme) => theme.palette.success.main }} />;
    }
    if (active) {
      return <FiberManualRecordIcon sx={{ ...iconStyle, color: (theme) => theme.palette.warning.main }} />;
    }
    return <RadioButtonUncheckedIcon sx={{ ...iconStyle, color: (theme) => theme.palette.grey[300] }} />;
  };

  return (
    <CustomStepper
      alternativeLabel
      connector={
        <StepConnector 
          sx={{
            '& .MuiStepConnector-line': {
              borderColor: theme => theme.palette.grey[300]
            },
            '&.Mui-completed .MuiStepConnector-line': {
              borderColor: theme => theme.palette.success.main
            }
          }}
        />
      }
    >
      {steps.map((step, index) => {
        const stepState = getStepState(step.id);
        const prevStepStatus = index > 0 ? stepsStatus?.[steps[index - 1]?.id] : null;
        
        // Only show completed connector if previous step is completed
        const isConnectorCompleted = prevStepStatus === 'completed';

        return (
          <Step 
            key={step.id}
            completed={stepState === 'completed'}
            active={stepState === 'active'}
            sx={{
              '& .MuiStepConnector-root': {
                '&.Mui-completed .MuiStepConnector-line': {
                  borderColor: theme => isConnectorCompleted ? theme.palette.success.main : theme.palette.grey[300]
                }
              }
            }}
          >
            <StepLabel 
              StepIconComponent={StepIcon}
              sx={{
                '& .MuiStepLabel-iconContainer': {
                  padding: '4px',
                  background: (theme) => theme.palette.background.paper,
                }
              }}
            >
              {step.label}
            </StepLabel>
          </Step>
        );
      })}
    </CustomStepper>
  );
};

export default AppStatusStepper; 